/**
 * @fileoverview JWT token utilities for authentication and authorization.
 */

import jwtDecode from "jwt-decode";
import {isPast, toDate} from "date-fns";

/**
 * Enumeration of token subject types for different authentication contexts.
 */
enum SubjectType {
  /**
   * Token for listing/entering own admission from dashboard/QR code.
   */
  ADMISSION_ENTRY = "ADMISSION_ENTRY",
  /**
   * Token for listing/entering own admissions from email link.
   */
  ADMISSION_EMAIL_ENTRY = "ADMISSION_EMAIL_ENTRY",
  /**
   * Token for working with admission (resident experience).
   */
  ADMISSION = "ADMISSION",
  /**
   * Token for the admin app.
   */
  USER = "USER",
}

/**
 * Type definition for JWT token payload structure.
 */
type Token = Readonly<{
  sub: string;
  exp: number;
  iat: number;
  subjectType: SubjectType;
  jti: string;
  single?: boolean;
}>;

/**
 * Factory function that creates token property getters.
 *
 * @param {keyof Token} key The token property key to extract.
 * @returns {Function} Function that extracts the specified property from a token.
 */
const getTokenKey = (key: keyof Token) => (token: string) =>
  jwtDecode<Token>(token)[key];

/**
 * Extracts the subject (user ID) from a JWT token.
 */
export const getTokenSub = getTokenKey("sub");

/**
 * Checks if a token is marked as single-use.
 */
export const getIsTokenSingleUse = getTokenKey("single");

/**
 * Extracts the subject type from a JWT token.
 */
const getTokenSubjectType = getTokenKey("subjectType");

/**
 * Extracts the expiration time from a JWT token.
 */
const getTokenExp = getTokenKey("exp");

/**
 * Factory function that creates token type checkers.
 *
 * @param {SubjectType} subjectType The subject type to check for.
 * @returns {Function} Function that checks if a token has the specified subject type.
 */
const isTokenType = (subjectType: SubjectType) => (token: string) =>
  getTokenSubjectType(token) === subjectType;

/**
 * Checks if a token is an admission token.
 */
export const isAdmissionToken = isTokenType(SubjectType.ADMISSION);

/**
 * Checks if a token is an admission entry token.
 */
export const isAdmissionEntryToken = isTokenType(SubjectType.ADMISSION_ENTRY);

/**
 * Checks if a token is an admission email entry token.
 */
export const isAdmissionEmailEntryToken = isTokenType(
  SubjectType.ADMISSION_EMAIL_ENTRY,
);

/**
 * Creates an Authorization header value from a JWT token.
 *
 * @param {string} token The JWT token.
 * @returns {string} The Authorization header value.
 */
export const makeAuthorizationHeader = (token: string | null | undefined) => (token ? `Bearer ${token}` : "");

/**
 * Validates if a JWT token is properly formatted and can be decoded.
 *
 * @param {string} token The JWT token to validate.
 * @returns {boolean} True if the token is valid, false otherwise.
 */
export const isTokenValid = (token: string) => {
  try {
    jwtDecode(token);
    return true;
  } catch {
    return false;
  }
};

/**
 * Checks if a JWT token has expired.
 *
 * @param {string} token The JWT token to check.
 * @returns {boolean} True if the token is expired, false otherwise.
 */
export const tokenIsExpired = (token: string) =>
  isPast(toDate(1000 * (getTokenExp(token) as number)));

/**
 * Gets the expiration time of a JWT token in milliseconds (Unix timestamp).
 *
 * @param {string} token The JWT token.
 * @returns {number} Expiration time in milliseconds (Unix timestamp).
 */
export const getTokenExpirationTime = (token: string): number => {
  const exp = getTokenExp(token) as number;
  return exp * 1000; // Convert from seconds to milliseconds
};

/**
 * Gets the time remaining until token expiration in milliseconds.
 *
 * @param {string} token The JWT token.
 * @returns {number} Time remaining in milliseconds. Returns 0 if token is expired or invalid.
 */
export const getTokenTimeUntilExpiration = (token: string): number => {
  if (!isTokenValid(token)) {
    return 0;
  }

  const expirationTime = getTokenExpirationTime(token);
  const now = Date.now();
  const remaining = expirationTime - now;

  return Math.max(0, remaining);
};

/**
 * Checks if a token should be refreshed proactively.
 * Token should be refreshed if it will expire within the specified threshold.
 *
 * @param {string} token The JWT token to check.
 * @param {number} refreshThresholdMs Threshold in milliseconds before expiration to trigger refresh. Default: 1 minute.
 * @returns {boolean} True if token should be refreshed, false otherwise.
 */
export const shouldRefreshToken = (
  token: string | null | undefined,
  refreshThresholdMs: number = 60 * 1000, // Default: 1 minute
): boolean => {
  if (!token || !isTokenValid(token)) {
    return false;
  }

  const timeUntilExpiration = getTokenTimeUntilExpiration(token);
  return timeUntilExpiration > 0 && timeUntilExpiration <= refreshThresholdMs;
};
